import { useCallback, useRef } from 'react';

import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@/_components';

type TruncatedTextWithTooltipProps = {
  text: string;
  className?: string;
  /** Width cap so truncation can occur (table cell should use min-w-0). */
  maxWidthClassName?: string;
};

const TOLERANCE_PX = 1;

let measureCanvas: HTMLCanvasElement | null = null;

function getCanvas2d(): CanvasRenderingContext2D | null {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  return measureCanvas.getContext('2d');
}

/**
 * Single-line ellipsis; shows a tooltip with the full string only when text overflows.
 * Tooltip visibility is decided lazily on hover via Tippy's `onShow` — no state or effects needed.
 */
export function TruncatedTextWithTooltip({
  text,
  className,
  maxWidthClassName = 'max-w-[290px]',
}: TruncatedTextWithTooltipProps) {
  const ref = useRef<HTMLSpanElement>(null);

  const handleShow = useCallback(() => {
    const el = ref.current;
    if (!el) return false;

    if (el.scrollWidth > el.clientWidth + TOLERANCE_PX) return;

    const ctx = getCanvas2d();
    if (!ctx) return;

    ctx.font = getComputedStyle(el).font;
    const textWidth = ctx.measureText(text).width;
    const style = getComputedStyle(el);
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    const available = el.clientWidth - padding;

    return textWidth > available + TOLERANCE_PX ? undefined : false;
  }, [text]);

  return (
    <Tooltip content={text} placement="top" zIndex={50} onShow={handleShow}>
      <span ref={ref} className={twMerge('block min-w-0 truncate', maxWidthClassName, className)}>
        {text}
      </span>
    </Tooltip>
  );
}
